import { useEffect, useMemo, useRef, useState } from "react";

import { SortOrder, type GetEquipmentPingsResponseBody } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { TableV2, Typography } from "@eisox/design-system";
import { ChevronDownIcon, ChevronUpIcon } from "@eisox/icons";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";

import { equipmentApi } from "~/api";
import { EMPTY_VALUE } from "~/constants/display.constant";
import type { InstantRange } from "~/helpers/day-range.helper";
import { m } from "~/paraglide/messages";

import { PING_KINDS } from "../connectivity-history.constant";
import { formatDrift, formatDuration } from "../helpers/connectivity-history.helper";

const COLUMN_COUNT = 4;
const ROW_HEIGHT_ESTIMATE = 41;
const OVERSCAN = 10;

interface ConnectivityPingsTableProps {
  buildingId: string;
  equipmentId: string;
  range: InstantRange;
  timezone: string;
}

const ConnectivityPingsTable: React.FC<ConnectivityPingsTableProps> = ({
  buildingId,
  equipmentId,
  range,
  timezone,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [order, setOrder] = useState(SortOrder.DESC);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<GetEquipmentPingsResponseBody>({
    ...equipmentApi.queries.pings(buildingId, equipmentId, { ...range, order }),
    initialPageParam: undefined,
    getNextPageParam: last => last.nextCursor,
  });

  const pings = useMemo(() => data?.pages.flatMap(page => page.data) ?? [], [data]);

  const virtualizer = useVirtualizer({
    count: pings.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: OVERSCAN,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom = virtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0);
  const lastRowIndex = virtualRows.at(-1)?.index;

  useEffect(() => {
    if (lastRowIndex === pings.length - 1 && hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [lastRowIndex, pings.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="min-h-0 flex-1">
      <TableV2.Container ref={scrollRef}>
        <TableV2.Root className="w-full table-fixed">
          <colgroup>
            <col className="md:w-60 w-40" />
            <col className="md:w-40 w-28" />
            <col className="md:w-40 w-28" />
            <col />
          </colgroup>
          <TableV2.Header className="top-0 sticky z-10 border-b-2 border-primary-darker-gray bg-primary-white">
            <TableV2.Row>
              <TableV2.Head className="pb-2.5 text-left">
                <button
                  type="button"
                  className="[&_svg]:ml-2.5 [&_svg]:size-3.25 flex cursor-pointer items-center [&_svg]:shrink-0 [&_svg]:text-primary-dark-gray"
                  onClick={() => setOrder(current => (current === SortOrder.DESC ? SortOrder.ASC : SortOrder.DESC))}
                >
                  {m.date()}
                  {order === SortOrder.DESC ? <ChevronDownIcon /> : <ChevronUpIcon />}
                </button>
              </TableV2.Head>
              <TableV2.Head className="pb-2.5 text-left">{m.connectivity_history_interval()}</TableV2.Head>
              <TableV2.Head className="pb-2.5 text-left">{m.connectivity_history_drift()}</TableV2.Head>
              <TableV2.Head className="pb-2.5 text-left">{m.type()}</TableV2.Head>
            </TableV2.Row>
          </TableV2.Header>
          <TableV2.Body>
            {data && pings.length === 0 && (
              <TableV2.Row>
                <TableV2.Cell colSpan={COLUMN_COUNT}>
                  <Typography className="py-10 text-center text-primary-dark-gray">{m.no_historic()}</Typography>
                </TableV2.Cell>
              </TableV2.Row>
            )}
            {paddingTop > 0 && <tr aria-hidden style={{ height: paddingTop }} />}
            {virtualRows.map(({ key, index }) => {
              const ping = pings[index];
              if (!ping) return null;
              const { date, gapMs, driftMs, kind } = ping;
              return (
                <TableV2.Row
                  key={key}
                  ref={virtualizer.measureElement}
                  data-index={index}
                  className="border-b border-primary-gray"
                >
                  <TableV2.Cell className="py-2.5 tabular-nums">
                    {dayjs(date).tz(timezone).format("L LTS")}
                  </TableV2.Cell>
                  <TableV2.Cell className="py-2.5 tabular-nums">
                    {gapMs === undefined ? EMPTY_VALUE : formatDuration(gapMs)}
                  </TableV2.Cell>
                  <TableV2.Cell className="py-2.5 tabular-nums">
                    {driftMs === undefined ? EMPTY_VALUE : formatDrift(driftMs)}
                  </TableV2.Cell>
                  <TableV2.Cell className="py-2.5">
                    <span className="gap-2.5 flex items-center">
                      <span
                        className="size-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: PING_KINDS[kind].color }}
                      />
                      {PING_KINDS[kind].label()}
                    </span>
                  </TableV2.Cell>
                </TableV2.Row>
              );
            })}
            {paddingBottom > 0 && <tr aria-hidden style={{ height: paddingBottom }} />}
          </TableV2.Body>
        </TableV2.Root>
      </TableV2.Container>
    </div>
  );
};

ConnectivityPingsTable.displayName = "ConnectivityPingsTable";

export { ConnectivityPingsTable };
